import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import SaveIcon from "@mui/icons-material/Save";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { CreateQuickSimulationRequest, FundSumDropMap, Simulation, SimulationChangeType, SimulationMimshakData, SimulationSource, SimulationStatus } from "../../../interfaces/DTO/simulation";
import { SimulationChangeDetectionUtil } from "../../../services/userTask/simulationChangeDetectionUtil";
import { NewExposureMap } from "../../../hooks/feeLab/usePathDragChanges";
import { simulationService } from "../../../services/SimulationService";
import { simulationMimshakDataService } from "../../../services/SimulationMimshakDataService";
import { AdjustedProductType } from "../../../interfaces/DTO/mislaka_xml_models";
import { validateExcludedItems } from "../../../services/simulationExcludedItemsHeuristic";
import { SnackbarType, useSnackbarStore } from "../../../stores/useSnackbarStore";
import SaveSimulationDialog from "./SaveSimulationDialog";
import { formatSimulationStatus } from "../../../utils/simulationUtil";



interface Props {
  originalRows: MimshakData[];
  modifiedRows: MimshakData[];
  fundSumDropMap: FundSumDropMap;
  simulationItems?: SimulationMimshakData[];
  newExposureMap?: NewExposureMap;
  selectedPeriodType: "REGULAR" | "PRE_ADVICE";
  contactId?: number;
  familyId?: number;
  simulation?: Simulation | null;
  source?: SimulationSource;
  onUpdateSimulation?: (name: string, notes: string, status: SimulationStatus) => void;
  onItemsReplaced?: (items: SimulationMimshakData[]) => void;
  excludedSimulationItems?: SimulationMimshakData[];
  excludedOriginalRows?: MimshakData[];
  excludedModifiedRows?: MimshakData[];
  allData?: MimshakData[];
  includedTypes?: AdjustedProductType[];
}

const SaveSimulationButton: React.FC<Props> = ({
  originalRows,
  modifiedRows,
  fundSumDropMap,
  simulationItems: simulationItemsProp,
  newExposureMap = {},
  selectedPeriodType,
  contactId,
  familyId,
  simulation: simulationProp,
  source,
  onUpdateSimulation,
  onItemsReplaced,
  excludedSimulationItems = [],
  excludedOriginalRows = [],
  excludedModifiedRows = [],
  allData,
  includedTypes,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [savedSimulation, setSavedSimulation] = useState<Simulation | null | undefined>(simulationProp);
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const isPreAdvice = selectedPeriodType === "PRE_ADVICE";

  const [localSimulationItems, setLocalSimulationItems] = useState<SimulationMimshakData[]>([]);
  useEffect(() => {
    if (!simulationItemsProp) {
      const items = SimulationChangeDetectionUtil.convertModifiedToSimulationItems(originalRows, modifiedRows, fundSumDropMap);
      setLocalSimulationItems(items);
    }
  }, [simulationItemsProp, originalRows, modifiedRows, fundSumDropMap]);

  const simulationItems = simulationItemsProp ?? localSimulationItems;

  // When a simulation is loaded, excludedSimulationItems already contains the excluded policies from DB.
  // Merge with excludedModifiedRows so any runtime changes (e.g. funds_sum reduction) are persisted.
  const mergedExcludedItems: SimulationMimshakData[] = useMemo(() => {
    if (excludedSimulationItems.length > 0) {
      const merged = excludedSimulationItems.map(simItem => {
        const modified = excludedModifiedRows.find(m => m.policy_number === simItem.policy_number && m.handler_code === simItem.handler_code);
        if (!modified) return simItem;
        return {
          ...simItem,
          ...modified,
          change_types: (modified as any)._changeTypes ?? simItem.change_types,
        };
      });
      if (merged.length < excludedSimulationItems.length) {
        console.error(
          "[SaveSimulationButton] mergedExcludedItems has fewer items than excludedSimulationItems — possible data loss.",
          { expected: excludedSimulationItems.length, got: merged.length }
        );
      }
      return merged;
    }
    return excludedOriginalRows.map(row => {
      const modified = excludedModifiedRows.find(m => m.policy_number === row.policy_number && m.handler_code === row.handler_code);
      const source = modified ?? row;
      const changeTypes = (source as any)._changeTypes ?? [SimulationChangeType.UNCHANGED];
      return {
        ...source,
        simulation_id: undefined as any,
        change_types: changeTypes,
        deposit_amount: 0,
        withdrawal_amount: 0,
        recurrent_deposit: null as number | null,
        transfer_in: null as number | null,
        transfer_out: null as number | null,
        transfer_out_to: null as string | null,
      };
    });
  }, [excludedSimulationItems, excludedOriginalRows, excludedModifiedRows]);

  const simulationKeys = new Set(simulationItems.map(i => `${i.policy_number}|${i.handler_code}`));
  const seenKeys = new Set<string>();
  const allItems = [...simulationItems, ...mergedExcludedItems.filter(i => !simulationKeys.has(`${i.policy_number}|${i.handler_code}`))]
    .map(({ id, simulation_id, ...rest }) => rest as SimulationMimshakData)
    .filter(i => {
      const key = `${i.policy_number}|${i.handler_code}|${i.product_type}`;
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });

  const reportedPeriod = originalRows[0]?.reported_period
    ?? allItems[0]?.reported_period;

  const simulationData = Object.keys(newExposureMap).length > 0
    ? { exposure_map: newExposureMap }
    : undefined;

  useEffect(() => {
    setSavedSimulation(simulationProp);
  }, [simulationProp]);

  const handleSaveNew = async (name: string, notes: string, status: SimulationStatus) => {
    const req: CreateQuickSimulationRequest = {
      simulation: {
        contact_id: contactId,
        family_id: familyId,
        reported_period: reportedPeriod,
        name,
        notes,
        status,
        is_pre_advice: isPreAdvice,
        data: simulationData,
      },
      items: allItems,
      create_tasks: false,
      source,
    };

    try {
      const response = await simulationService.quickCreate(req);
      if (response.simulation) {
        setSavedSimulation(response.simulation);
      }
      showSnackbar("סימולציה נשמרה בהצלחה", SnackbarType.SUCCESS);
    } catch (e: any) {
      showSnackbar("משהו השתבש. לא נשמרה סימולציה", SnackbarType.ERROR);
      console.error(e);
      throw e;
    }
  };

  const [dialogMode, setDialogMode] = useState<"new" | "metadata-only">("new");

  const handleMetadataOnly = async (name: string, notes: string, status: SimulationStatus) => {
    if (!savedSimulation?.id) return;
    try {
      const updated = await simulationService.patchMetadata(savedSimulation.id, { name, notes, status });
      setSavedSimulation(updated);
      onUpdateSimulation?.(name, notes, status);
      showSnackbar("סימולציה עודכנה בהצלחה", SnackbarType.SUCCESS);
    } catch (e: any) {
      showSnackbar("משהו השתבש. לא עודכנה סימולציה", SnackbarType.ERROR);
      console.error(e);
      throw e;
    }
  };

  const handleUpdateExisting = async (name: string, notes: string, status: SimulationStatus) => {
    if (!savedSimulation?.id) return;
    try {
      const [updated] = await Promise.all([
        simulationService.patchMetadata(savedSimulation.id, { name, notes, status, data: simulationData }),
        simulationMimshakDataService.replaceBySimulationId(savedSimulation.id, allItems, source),
      ]);
      setSavedSimulation(updated);
      onUpdateSimulation?.(name, notes, status);
      // Reload fresh items (with new IDs) so blur-save works correctly after update
      const freshItems = await simulationMimshakDataService.getBySimulationId(savedSimulation.id);
      onItemsReplaced?.(freshItems);
      showSnackbar("סימולציה עודכנה בהצלחה", SnackbarType.SUCCESS);
    } catch (e: any) {
      showSnackbar("משהו השתבש. לא עודכנה סימולציה", SnackbarType.ERROR);
      console.error(e);
      throw e;
    }
  };

  const isApproved = savedSimulation?.status === SimulationStatus.APPROVED;
  const canApprove = savedSimulation?.status === SimulationStatus.SELECTED;
  const approvePreAdvice = savedSimulation?.is_pre_advice ?? isPreAdvice;

  const handleApprove = async () => {
    if (!savedSimulation?.id) return;
    setApproving(true);
    try {
      const response = await simulationService.approve(savedSimulation.id);
      if (response.simulation) {
        setSavedSimulation(response.simulation);
        onUpdateSimulation?.(response.simulation.name, response.simulation.notes ?? "", response.simulation.status);
      }
      setConfirmApproveOpen(false);
      showSnackbar(`הסימולציה אושרה לביצוע – נוצרו ${response.tasks_created} משימות`, SnackbarType.SUCCESS);
    } catch (e: any) {
      showSnackbar("משהו השתבש. הסימולציה לא אושרה לביצוע", SnackbarType.ERROR);
      console.error(e);
    } finally {
      setApproving(false);
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} dir="rtl">
        {savedSimulation && (
          <>
            <Typography variant="body2" color="text.secondary">
              {savedSimulation.name}
            </Typography>
            {savedSimulation.status && (
              <Chip
                label={formatSimulationStatus(savedSimulation.status)}
                size="small"
                variant="outlined"
              />
            )}
            <Tooltip title="ערוך שם וסטטוס">
              <IconButton size="small" onClick={() => { setDialogMode("metadata-only"); setDialogOpen(true); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        {savedSimulation ? (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => { setDialogMode("new"); setDialogOpen(true); }}
              disabled={simulationItems.length === 0}
            >
              שמור כחדשה
            </Button>
            <Tooltip title={isApproved ? "הסימולציה אושרה לביצוע ונוצרו ממנה משימות. לשינויים יש לשמור כסימולציה חדשה" : ""}>
              {/* span – כדי שה-Tooltip יעבוד גם כשהכפתור מושבת */}
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon sx={{ ml: 1, color: 'grey.400' }} />}
                  onClick={() => setConfirmUpdateOpen(true)}
                  disabled={isApproved}
                >
                  שמור שינויים
                </Button>
              </span>
            </Tooltip>
            {canApprove && (
              <Button
                variant="contained"
                color="success"
                startIcon={<TaskAltIcon sx={{ ml: 1 }} />}
                onClick={() => setConfirmApproveOpen(true)}
              >
                אשר לביצוע
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={() => { setDialogMode("new"); setDialogOpen(true); }}
            disabled={simulationItems.length === 0}
          >
            שמור סימולציה
          </Button>
        )}
      </Box>
      <Dialog open={confirmUpdateOpen} onClose={() => setConfirmUpdateOpen(false)} dir="rtl">
        <DialogTitle>שמור שינויים</DialogTitle>
        <DialogContent>
          <DialogContentText>
            לשמור את השינויים שבוצעו בסימולציה <strong>{savedSimulation?.name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUpdateOpen(false)}>ביטול</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const originalPolicyNumbers = new Set(originalRows.map((r) => r.policy_number));
              const hasNullType = modifiedRows.some(
                (r) => !originalPolicyNumbers.has(r.policy_number) && (r.adjusted_product_type === null || r.adjusted_product_type === undefined)
              );
              if (hasNullType) {
                setConfirmUpdateOpen(false);
                showSnackbar("יש מוצר חדש ללא סוג מוצר. יש לבחור סוג מוצר לפני השמירה.", SnackbarType.ERROR);
                return;
              }
              if (allData && includedTypes) {
                const check = validateExcludedItems(allData, includedTypes, mergedExcludedItems);
                if (!check.valid) {
                  setConfirmUpdateOpen(false);
                  alert(
                    "התגלתה בעיה בנתוני הסימולציה: חסרים פריטים שאמורים להישמר.\n" +
                    `צפוי לפחות ${check.expectedMinCount} רשומות מוצרים נלווים, נמצאו ${check.actualCount}.\n\n` +
                    "השמירה נחסמה כדי למנוע דריסת נתונים קיימים.\n" +
                    "נסה לרענן את הדף ונסה שוב.\nאנו מצטערים על האי נוחות."
                  );
                  return;
                }
              }
              setConfirmUpdateOpen(false);
              await handleUpdateExisting(
                savedSimulation!.name,
                savedSimulation!.notes ?? "",
                savedSimulation!.status,
              );
            }}
          >
            שמור
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmApproveOpen} onClose={() => !approving && setConfirmApproveOpen(false)} dir="rtl">
        <DialogTitle>אישור לביצוע</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            לאשר לביצוע את הסימולציה <strong>{savedSimulation?.name}</strong>?
            <ul>
              <li>לכל מוצר עם שינוי תיווצר משימה לכל שינוי.</li>
              {approvePreAdvice ? (
                <li>טרום ייעוץ: לכל מוצר ללא שינוי תיווצר משימת מינוי סוכן.</li>
              ) : (
                <li>מוצרים ללא שינוי לא יהפכו למשימה.</li>
              )}
            </ul>
            המשימות נוצרות מהסימולציה כפי שנשמרה – שינויים שלא נשמרו לא ייכללו.
            אחרי האישור לא ניתן לשמור שינויים על הסימולציה הזו (רק כסימולציה חדשה).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApproveOpen(false)} disabled={approving}>ביטול</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={approving}>
            {approving ? "מאשר..." : "אשר לביצוע"}
          </Button>
        </DialogActions>
      </Dialog>
      <SaveSimulationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        existingSimulation={dialogMode === "metadata-only" ? savedSimulation : null}
        metadataOnly={dialogMode === "metadata-only"}
        modifiedRows={modifiedRows}
        originalRows={originalRows}
        onSaveNew={handleSaveNew}
        onUpdateSimulation={dialogMode === "metadata-only" ? handleMetadataOnly : undefined}
      />
    </>
  );
};

export default SaveSimulationButton;
